import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  Circle,
  Square,
  Play,
  Loader2,
  Video,
  Download,
  Trash2,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

// ============================================
// RECORDING STATUS BADGE
// ============================================

export function MeetingRecordingStatus({ status, startedAt }) {
  const [elapsed, setElapsed] = useState('00:00');

  useEffect(() => {
    if (status !== 'recording' || !startedAt) return;

    const updateElapsed = () => {
      const start = new Date(startedAt);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      const mins = Math.floor(diff / 60).toString().padStart(2, '0');
      const secs = (diff % 60).toString().padStart(2, '0');
      setElapsed(`${mins}:${secs}`);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [status, startedAt]);

  if (status === 'recording') {
    return (
      <Badge variant="destructive" className="gap-1 animate-pulse">
        <Circle className="h-2 w-2 fill-current" />
        REC {elapsed}
      </Badge>
    );
  }

  if (status === 'processing') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Processing...
      </Badge>
    );
  }

  return null;
}

// ============================================
// RECORDING CONTROLS (IN-CALL)
// ============================================

export function MeetingRecordingControls({
  meetingId,
  isHost,
  onStatusChange,
  className = "",
}) {
  const [status, setStatus] = useState('idle');
  const [loading, setLoading] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  // Check current recording status on mount
  useEffect(() => {
    let cancelled = false;
    const checkStatus = async () => {
      try {
        const response = await api.get(`/meeting-recordings/meeting/${meetingId}/current`);
        if (cancelled) return;
        if (response.recording) {
          setStatus(response.recording.status);
          setStartedAt(response.recording.started_at);
        }
      } catch (error) {
        console.error('Error checking meeting recording status:', error);
      }
    };
    checkStatus();
    return () => { cancelled = true; };
  }, [meetingId]);

  // Poll during processing and for non-hosts to detect recording start/stop
  useEffect(() => {
    const shouldPoll = status === 'processing' || !isHost;
    if (!shouldPoll) return;

    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/meeting-recordings/meeting/${meetingId}/current`);
        if (response.recording) {
          const newStatus = response.recording.status;
          if (newStatus !== status) {
            setStatus(newStatus);
            setStartedAt(response.recording.started_at);
            onStatusChange?.(newStatus);
          }
        } else if (status === 'recording' || status === 'processing') {
          setStatus('idle');
          setStartedAt(null);
          onStatusChange?.('idle');
        }
      } catch {
        // silent
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [meetingId, status, isHost, onStatusChange]);

  // Notify parent on status change
  useEffect(() => {
    onStatusChange?.(status);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = async () => {
    setLoading(true);
    try {
      const response = await api.post('/meeting-recordings/start', { meetingId });
      setStatus('recording');
      setStartedAt(response.startedAt || new Date().toISOString());
      toast.success('Recording started');
    } catch (error) {
      toast.error(error.message || 'Failed to start recording');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      await api.post(`/meeting-recordings/stop/${meetingId}`);
      setStatus('processing');
      toast.success('Recording stopped. Processing...');
    } catch (error) {
      toast.error(error.message || 'Failed to stop recording');
    } finally {
      setLoading(false);
    }
  };

  const isRecording = status === 'recording';

  // Non-hosts: show indicator only
  if (!isHost) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <MeetingRecordingStatus status={status} startedAt={startedAt} />
      </div>
    );
  }

  // Host: full controls
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <MeetingRecordingStatus status={status} startedAt={startedAt} />

      <Button
        variant={isRecording ? "destructive" : "outline"}
        size="sm"
        onClick={isRecording ? handleStop : handleStart}
        disabled={loading || status === 'processing'}
        className="gap-2"
        data-testid="meeting-recording-toggle"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isRecording ? (
          <Square className="h-4 w-4 fill-current" />
        ) : (
          <Circle className="h-4 w-4 text-red-500 fill-red-500" />
        )}
        <span className="hidden sm:inline">
          {isRecording ? 'Stop' : 'Record'}
        </span>
      </Button>
    </div>
  );
}

// ============================================
// RECORDINGS LIST (PAST RECORDINGS)
// ============================================

export function MeetingRecordingsList({ meetingId }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecording, setSelectedRecording] = useState(null);

  useEffect(() => {
    const fetchRecordings = async () => {
      try {
        const response = await api.get(`/meeting-recordings/meeting/${meetingId}`);
        setRecordings(response.recordings || []);
      } catch (error) {
        console.error('Error fetching meeting recordings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecordings();
  }, [meetingId]);

  const handleDelete = async (recordingId) => {
    if (!confirm('Delete this recording?')) return;
    try {
      await api.delete(`/meeting-recordings/${recordingId}`);
      setRecordings(prev => prev.filter(r => r._id !== recordingId));
      toast.success('Recording deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete recording');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '--';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div className="text-center p-4 text-muted-foreground">
        <Video className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No recordings yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {recordings.map(recording => (
        <div
          key={recording._id}
          className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-card"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="p-2 rounded-lg bg-muted flex-shrink-0">
              <Video className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                {new Date(recording.started_at).toLocaleDateString()}
                {' '}
                {new Date(recording.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                <Clock className="h-3 w-3 flex-shrink-0" />
                <span>{formatDuration(recording.duration_seconds)}</span>
                <span className="hidden xs:inline">•</span>
                <span className="hidden xs:inline">{formatSize(recording.file_size_bytes)}</span>
                <Badge variant={recording.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                  {recording.status}
                </Badge>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
            {recording.status === 'completed' && recording.playbackUrl && (
              <>
                <Button variant="ghost" size="icon" onClick={() => setSelectedRecording(recording)} title="Play">
                  <Play className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" asChild title="Download">
                  <a href={recording.playbackUrl} download>
                    <Download className="h-4 w-4" />
                  </a>
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" onClick={() => handleDelete(recording._id)} title="Delete">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      ))}

      <Dialog open={!!selectedRecording} onOpenChange={() => setSelectedRecording(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Recording Playback</DialogTitle>
            <DialogDescription>
              {selectedRecording && new Date(selectedRecording.started_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {selectedRecording?.playbackUrl && (
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <video src={selectedRecording.playbackUrl} controls className="w-full h-full" autoPlay />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MeetingRecordingControls;
